import { faWarning, IconDefinition } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import Button from 'components/base/Button';
import React, { createContext, useState, useContext, ReactNode } from 'react';
import { Modal } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';

// Options for the confirm dialog
interface ConfirmOptions {
  title?: string;
  icon?: IconDefinition;
}

// Context type definition
interface ConfirmContextType {
  confirm: (message: string, options?: ConfirmOptions) => Promise<boolean>;
}

// State type definition
interface ConfirmState extends ConfirmOptions {
  isOpen: boolean;
  message: string;
  resolve: ((value: boolean) => void) | null;
}

// Props type definition
interface ConfirmProviderProps {
  children: ReactNode;
}

const ConfirmContext = createContext<ConfirmContextType | null>(null);

export function ConfirmProvider({ children }: ConfirmProviderProps) {
  const { t } = useTranslation();
  const [confirmState, setConfirmState] = useState<ConfirmState>({
    isOpen: false,
    message: '',
    resolve: null
  });

  const confirm = (message: string, options: ConfirmOptions = {}): Promise<boolean> => {
    const config = {
      message: message,
      title: options.title || t('confirmDelete'),
      icon: options.icon || faWarning
    };

    return new Promise<boolean>(resolve => {
      setConfirmState({ isOpen: true, ...config, resolve });
    });
  };

  const handleConfirm = (): void => {
    confirmState.resolve?.(true);
    setConfirmState({ ...confirmState, isOpen: false });
  };

  const handleCancel = (): void => {
    confirmState.resolve?.(false);
    setConfirmState({ ...confirmState, isOpen: false });
  };

  return (
    <ConfirmContext.Provider value={{ confirm }}>
      {children}

      <Modal show={confirmState.isOpen} onHide={handleCancel} centered>
        <Modal.Header closeButton>
          <Modal.Title>
            {confirmState.icon && <FontAwesomeIcon icon={confirmState.icon} className="me-2" />}
            {confirmState.title}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body style={{ whiteSpace: 'pre-line' }}>{confirmState.message}</Modal.Body>
        <Modal.Footer>
          <Button variant="phoenix-primary" onClick={handleCancel}>
            {t('cancel')}
          </Button>
          <Button variant="primary" onClick={handleConfirm}>
            {t('confirm')}
          </Button>
        </Modal.Footer>
      </Modal>
    </ConfirmContext.Provider>
  );
}

export const useConfirm = (): ConfirmContextType => {
  const context = useContext(ConfirmContext);
  if (!context) {
    throw new Error('useConfirm must be used within a ConfirmProvider');
  }
  return context;
};
